import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { discoverAccountsThunk, getIdentifiersThunk } from "../features/account/accountThunk";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";

const DiscoveryPrompt: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { mobileNumber } = useAppSelector((s) => s.auth);
    const { selectedFip } = useAppSelector((s) => s.fip);
    const { loading, error } = useAppSelector((s) => s.account);

    // Trigger background identifier search on mount
    React.useEffect(() => {
        if (selectedFip) {
            dispatch(getIdentifiersThunk({ fipId: selectedFip.FIP_ID }));
        }
    }, [dispatch, selectedFip]);

    const handleDiscover = async () => {
        if (!selectedFip) return;

        const resultAction = await dispatch(
            discoverAccountsThunk({
                fipId: selectedFip.FIP_ID,
                fiTypes: ["DEPOSIT"] // Default FI type, thunk will handle discovery
            })
        );

        if (discoverAccountsThunk.fulfilled.match(resultAction)) {
            navigate("/userAccounts");
        }
    };

    const maskedMobile = mobileNumber ? "+91 " + mobileNumber : "";

    return (
        <div className="flex flex-col min-h-screen bg-fin-bg">
            <div className="flex-1 flex flex-col p-6 items-center w-full max-w-sm mx-auto mt-10">

                {/* Bank Icon / Logo */}
                <div className="h-16 w-16 bg-fin-surface rounded-full flex items-center justify-center mb-6 border border-fin-border overflow-hidden p-2">
                    {selectedFip?.LOGO_URL ? (
                        <img src={selectedFip.LOGO_URL} alt={selectedFip.FIP_NAME} className="h-full w-full object-contain" />
                    ) : (
                        <span className="text-xl font-bold text-fin-primary">{selectedFip?.FIP_NAME?.substring(0, 1)}</span>
                    )}
                </div>

                <div className="text-center mb-8 w-full">
                    <h2 className="text-xl font-bold text-white leading-snug">
                        Discover accounts at {selectedFip?.FIP_NAME}
                    </h2>
                    <p className="text-sm text-fin-slate mt-3 leading-relaxed">
                        We will search for your financial accounts linked to your registered mobile number.
                    </p>
                </div>

                {/* Mobile Confirmation Card */}
                <Card className="w-full bg-fin-surface border-fin-border shadow-none p-5 mb-8">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-[13px] font-semibold text-fin-slate uppercase tracking-wider">Registered Mobile</span>
                        <button
                            onClick={() => navigate("/alternateNumber")}
                            className="text-xs font-bold text-fin-primary hover:text-fin-400"
                        >
                            Change
                        </button>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="h-2 w-2 rounded-full bg-fin-primary animate-pulse"></div>
                        <p className="text-lg font-mono font-bold text-white tracking-tight">{maskedMobile}</p>
                    </div>
                    <p className="text-xs text-fin-slate/60 mt-4 italic">
                        "Make sure your accounts are linked to this number for a successful discovery."
                    </p>
                </Card>

                {error && (
                    <div className="w-full p-4 mb-6 bg-red-900/10 border border-red-900/20 rounded-lg">
                        <p className="text-sm text-red-400 font-medium">{error}</p>
                    </div>
                )}
            </div>

            {/* Bottom Actions */}
            <div className="w-full bg-fin-surface border-t border-fin-border p-4 pb-8 mt-auto">
                <div className="max-w-sm mx-auto flex flex-col gap-3">
                    <Button
                        variant="primary"
                        fullWidth
                        isLoading={loading}
                        onClick={handleDiscover}
                        className="py-4 text-[16px] font-bold"
                    >
                        {loading ? "Searching..." : "Discover Accounts"}
                    </Button>
                    <Button
                        variant="ghost"
                        fullWidth
                        disabled={loading}
                        onClick={() => navigate("/userBank")}
                        className="py-3 text-fin-slate font-semibold"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DiscoveryPrompt;
